import type { z } from 'zod';

/**
 * Endpoint descriptors.
 *
 * Every route in the platform is declared once, here, as a value: method, path,
 * the schemas for its path parameters, query, body and response, and whether it
 * requires a client-supplied idempotency key. The server validates against the
 * same descriptor it serves, and the client infers its argument and return types
 * from it - one declaration, both sides of the wire.
 *
 * This replaces hono/client deliberately. tech-stack section 2 confines Hono to
 * apps/api-worker ("no other package imports it"), which a frontend importing
 * hono/client would break; a descriptor plus a small fetch wrapper keeps the
 * typing and keeps the frontends inside their dependency budget (section 11).
 */

/**
 * The header that carries the caller's idempotency key on every mutation
 * declaring `idempotent: true` below (PRD PAY-006, NFR-003).
 *
 * Here rather than in one mount's module, because it is on all of them: the
 * client sends it, `bind.ts` claims the key under it before the handler runs,
 * CORS grants it on every preflight, and the demo generator's own sweep sets
 * it. It lived in the guest contract with no caller at all until the audit's
 * finding 56, while the value was written out as a literal at those four
 * sites - and `cors.ts` and `bind.ts` disagreeing about a header name is a
 * whole class of CORS bug that no test on either side can see alone.
 *
 * Lower case deliberately: `Headers` is case-insensitive but a plain object of
 * request headers is not, and this string is used as a key in both.
 */
export const IDEMPOTENCY_HEADER = 'idempotency-key';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/** Which mount serves the route. Maps 1:1 onto the sub-apps in tech-stack section 6. */
export type Mount = 'guest' | 'partner' | 'bakery' | 'internal' | 'webhooks' | 'site' | 'release';

/** Mount prefixes. Nothing may hardcode these strings anywhere else. */
export const MOUNTS = {
  guest: '/g',
  partner: '/c',
  bakery: '/b',
  internal: '/internal',
  webhooks: '/webhooks',
  /** The public marketing site's one write; no identity, bot-challenged. */
  site: '/site',
  /**
   * The production release door (OPS-187): served on `release.<domain>` only,
   * behind Cloudflare Access and a per-request release-key signature.
   */
  release: '/release',
} as const satisfies Record<Mount, string>;

/**
 * The one role a route is restricted to beyond its mount, when it is (PAY-032,
 * OPS-003): `INTERNAL_FINANCE`, the finance approver - releasing or holding
 * money, the stored document and its reissue, the accountant's settings.
 *
 * Declared on the DESCRIPTOR and enforced once, by the worker's `bindEndpoint`
 * before the handler runs, so the gated set is readable in one place
 * (`endpoints.test.ts` lists it) instead of being a call each handler had to
 * remember - there were two copies of that check and nine calls to them.
 */
export type EndpointAccess = 'INTERNAL_FINANCE';

export interface Endpoint<
  TResponse extends z.ZodType = z.ZodType,
  TParams extends z.ZodType | undefined = z.ZodType | undefined,
  TQuery extends z.ZodType | undefined = z.ZodType | undefined,
  TBody extends z.ZodType | undefined = z.ZodType | undefined,
> {
  readonly method: HttpMethod;
  /** Path with `:name` placeholders, including the mount prefix. */
  readonly path: string;
  readonly mount: Mount;
  readonly params: TParams;
  readonly query: TQuery;
  readonly body: TBody;
  readonly response: TResponse;
  /** The role this route is restricted to beyond its mount (`EndpointAccess`); absent for most. */
  readonly access?: EndpointAccess;
  /**
   * True when the route creates an order, a charge, a dispatch, a refund or a
   * payout. The caller must send `Idempotency-Key`; the server enforces it with a
   * unique constraint in Postgres (PRD PAY-006, NFR-003).
   */
  readonly idempotent: boolean;
  /** Short human description; also the source of the route's log label. */
  readonly summary: string;
}

/** Everything a descriptor states whatever its method. */
interface EndpointConfigBase<
  TResponse extends z.ZodType,
  TParams extends z.ZodType | undefined,
  TQuery extends z.ZodType | undefined,
  TBody extends z.ZodType | undefined,
> {
  /** Path with `:name` placeholders, including the mount prefix. */
  readonly path: string;
  readonly mount: Mount;
  readonly summary: string;
  readonly response: TResponse;
  readonly params?: TParams;
  readonly query?: TQuery;
  readonly body?: TBody;
  readonly access?: EndpointAccess;
}

/**
 * A read states no idempotency: the header is for mutations, and a GET is safe
 * by definition. Declaring the field here at all would let a descriptor write
 * `idempotent: false` on a GET, which reads as a decision somebody made rather
 * than as the only thing a GET can be.
 */
type ReadEndpointConfig<
  TResponse extends z.ZodType,
  TParams extends z.ZodType | undefined,
  TQuery extends z.ZodType | undefined,
  TBody extends z.ZodType | undefined,
> = EndpointConfigBase<TResponse, TParams, TQuery, TBody> & { readonly method: 'GET' };

/**
 * A mutation MUST state it, and that is the whole point of the split (PAY-006;
 * refactoring audit finding 42).
 *
 * `idempotent` was optional and defaulted to `false`, so the decision was made
 * by omission: a new POST that moves money was non-idempotent unless somebody
 * remembered, and nothing in the diff said so. Sixty-eight of the registry's
 * hundred and fifty-four mutations stated nothing at all, and the only thing
 * checking was a literal list of ten endpoint names in `endpoints.test.ts`.
 * Requiring the field moves the check from a list nobody maintains to the
 * typechecker, which reads every descriptor every time - and puts the answer in
 * the diff of the PR that adds the route.
 */
type MutationEndpointConfig<
  TResponse extends z.ZodType,
  TParams extends z.ZodType | undefined,
  TQuery extends z.ZodType | undefined,
  TBody extends z.ZodType | undefined,
> = EndpointConfigBase<TResponse, TParams, TQuery, TBody> & {
  readonly method: Exclude<HttpMethod, 'GET'>;
  readonly idempotent: boolean;
};

export function defineEndpoint<
  TResponse extends z.ZodType,
  TParams extends z.ZodType | undefined = undefined,
  TQuery extends z.ZodType | undefined = undefined,
  TBody extends z.ZodType | undefined = undefined,
>(
  config:
    | ReadEndpointConfig<TResponse, TParams, TQuery, TBody>
    | MutationEndpointConfig<TResponse, TParams, TQuery, TBody>,
): Endpoint<TResponse, TParams, TQuery, TBody> {
  return {
    method: config.method,
    path: config.path,
    mount: config.mount,
    summary: config.summary,
    response: config.response,
    params: config.params as TParams,
    query: config.query as TQuery,
    body: config.body as TBody,
    idempotent: config.method === 'GET' ? false : config.idempotent,
    ...(config.access === undefined ? {} : { access: config.access }),
  };
}

type InferOrNever<T> = [T] extends [z.ZodType] ? z.infer<T> : never;

export type EndpointResponse<E> =
  E extends Endpoint<infer R, infer _P, infer _Q, infer _B> ? z.infer<R> : never;
export type EndpointParams<E> =
  E extends Endpoint<infer _R, infer P, infer _Q, infer _B> ? InferOrNever<P> : never;
export type EndpointQuery<E> =
  E extends Endpoint<infer _R, infer _P, infer Q, infer _B> ? InferOrNever<Q> : never;
export type EndpointBody<E> =
  E extends Endpoint<infer _R, infer _P, infer _Q, infer B> ? InferOrNever<B> : never;

/** `{ params: T }` when the endpoint declares params, otherwise nothing to pass. */
export type FieldIfPresent<K extends string, T> = [T] extends [never]
  ? Partial<Record<K, never>>
  : { [P in K]: T };

/** Fills `:name` placeholders and returns the concrete path. */
export function buildPath(path: string, params: Record<string, string | number> = {}): string {
  return path.replace(/:([A-Za-z0-9_]+)/g, (_match, key: string) => {
    const value = params[key];
    if (value === undefined) {
      throw new Error(`Missing path parameter "${key}" for "${path}"`);
    }
    return encodeURIComponent(String(value));
  });
}

/** Serializes a query object, dropping undefined and expanding arrays. */
export function buildQuery(query: Record<string, unknown> | undefined): string {
  if (!query) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const entry of value) search.append(key, String(entry));
    } else {
      search.append(key, String(value));
    }
  }
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}
